import type { ApiPaginatedResponse, ApiResponse } from '@bcube/shared';
import { api } from '@/lib/api';
import type {
  IConfigurationProfile,
  IConfigurationProfileHistory,
} from '../../types/production/configuration-profile';

const transformDates = <
  T extends {
    created_at?: string | Date;
    updated_at?: string | Date | null;
    validated_at?: string | Date | null;
    inactivated_at?: string | Date | null;
  },
>(
  obj: T,
): T => {
  if (!obj) return obj;
  const result = { ...obj };
  if (result.created_at && typeof result.created_at === 'string') {
    result.created_at = new Date(result.created_at);
  }
  if (result.updated_at && typeof result.updated_at === 'string') {
    result.updated_at = new Date(result.updated_at);
  }
  if (result.validated_at && typeof result.validated_at === 'string') {
    result.validated_at = new Date(result.validated_at);
  }
  if (result.inactivated_at && typeof result.inactivated_at === 'string') {
    result.inactivated_at = new Date(result.inactivated_at);
  }
  return result;
};

export interface PaginatedResponse<T> {
  docs: T[];
  total: number;
  page: number;
  pages: number;
  limit: number;
}

export interface ConfigProfileStats {
  total: number;
  validated_by_human: number;
  validated_by_system: number;
  pending: number;
}

export interface ConfigProfileQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  type?: string;
  technology_system_name?: string;
  technology_id?: string;
  client_id?: string;
  status?: 'ATIVO' | 'INATIVO' | 'AGUARDANDO_VALIDACAO';
  created_at_from?: string;
  created_at_to?: string;
  updated_at_from?: string;
  updated_at_to?: string;
}

export interface CreateConfigProfileDto {
  name: string;
  type: string;
  technology_id: string;
  technology_system_name: string;
  client_id?: string;
  manual_client?: string;
  manual_client_register?: boolean;
  config: {
    general?: unknown;
    specific?: unknown;
  };
}

export interface UpdateConfigProfileDto {
  name?: string;
  type?: string;
  technology_id?: string;
  client_id?: string;
  manual_client?: string;
  manual_client_register?: boolean;
  config?: {
    general?: unknown;
    specific?: unknown;
  };
}

export const configurationProfileApi = {
  findMany: async (
    params?: ConfigProfileQueryParams,
  ): Promise<PaginatedResponse<IConfigurationProfile>> => {
    const { data } = await api.get<ApiPaginatedResponse<IConfigurationProfile>>(
      '/production/configuration-profiles',
      { params },
    );
    return {
      docs: data.data.map(transformDates),
      total: data.meta.total,
      page: data.meta.page,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  getStats: async (): Promise<ConfigProfileStats> => {
    const { data } = await api.get<ApiResponse<ConfigProfileStats>>(
      '/production/configuration-profiles/stats',
    );
    return data.data;
  },

  findOne: async (id: string): Promise<IConfigurationProfile> => {
    const { data } = await api.get<ApiResponse<IConfigurationProfile>>(
      `/production/configuration-profiles/${id}`,
    );
    return transformDates(data.data);
  },

  create: async (dto: CreateConfigProfileDto): Promise<IConfigurationProfile> => {
    const { data } = await api.post<ApiResponse<IConfigurationProfile>>(
      '/production/configuration-profiles',
      dto,
    );
    return transformDates(data.data);
  },

  update: async (id: string, dto: UpdateConfigProfileDto): Promise<IConfigurationProfile> => {
    const { data } = await api.patch<ApiResponse<IConfigurationProfile>>(
      `/production/configuration-profiles/${id}`,
      dto,
    );
    return transformDates(data.data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/production/configuration-profiles/${id}`);
  },

  validateProfile: async (id: string): Promise<IConfigurationProfile> => {
    const { data } = await api.post<ApiResponse<IConfigurationProfile>>(
      `/production/configuration-profiles/${id}/validate`,
    );
    return transformDates(data.data);
  },

  inactivateProfile: async (id: string): Promise<IConfigurationProfile> => {
    const { data } = await api.patch<ApiResponse<IConfigurationProfile>>(
      `/production/configuration-profiles/${id}/inactivate`,
    );
    return transformDates(data.data);
  },

  replaceProfile: async (id: string): Promise<IConfigurationProfile> => {
    const { data } = await api.post<ApiResponse<IConfigurationProfile>>(
      `/production/configuration-profiles/${id}/replace`,
    );
    return transformDates(data.data);
  },

  findHistory: async (
    id: string,
    params?: Pick<ConfigProfileQueryParams, 'page' | 'limit'>,
  ): Promise<PaginatedResponse<IConfigurationProfileHistory>> => {
    const { data } = await api.get<ApiPaginatedResponse<IConfigurationProfileHistory>>(
      `/production/configuration-profiles/${id}/history`,
      { params },
    );
    return {
      docs: data.data.map(transformDates),
      total: data.meta.total,
      page: data.meta.page,
      pages: data.meta.pages,
      limit: data.meta.limit,
    };
  },

  cloneProfile: async (id: string): Promise<IConfigurationProfile> => {
    const { data } = await api.post<ApiResponse<IConfigurationProfile>>(
      `/production/configuration-profiles/${id}/clone`,
    );
    return transformDates(data.data);
  },

  findByTechnology: async (technologyId: string): Promise<IConfigurationProfile[]> => {
    const { data } = await api.get<ApiPaginatedResponse<IConfigurationProfile>>(
      '/production/configuration-profiles',
      {
        params: { technology_id: technologyId, limit: 100 },
      },
    );
    return data.data.map(transformDates);
  },
};
